import { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { FieldValues } from 'react-hook-form';
import Card from '@/components/Card';
import { Input } from '@/components/Controls/Input';
import { Datatable } from '@/components/Datatable/Datatable';
import { TableViewProps } from '@/type/listing.types';
import { TableTitle } from '@/components/Header/TableTitle';
import Loader from '@/components/Loading/Loader';
import { ClearButton } from '@/components/Custom/Button/ClearButton';
import { useHistoryFilter } from '@/hooks/filters/history-filter.hooks';
import { HistoryType } from '@/entities/history';
import { useListAllGroups } from '@/hooks/group.hooks';
import { useListAllCompanies } from '@/hooks/company.hooks';
import { useListAllBranches } from '@/hooks/branch.hooks';
import { mapFilterSelect } from '@/utils/map.utils';
import {
  getInspectionModerationStatus,
  getInspectionValidationStatus,
} from '@/utils/constant.utils';
import VehicleCheckFilter from '@/components/Custom/Filter/VehicleCheckFilter';
import { useUserRole } from '@/hooks/auth.hooks';
import { ActionUserType } from '@/api/entities/vehicule-check';
import { usePageSize } from '@/hooks/page-size.hooks';
import { RichSelect } from '@/components/Controls/RichSelect';
import {
  AdvancedFilterPanel,
  AdvancedFilterToggle,
} from '@/components/Custom/Filter/FilterKit';
import { INSPECTION_TIPS } from '@/docs/tips/inspections';
import {
  getClientTypeFilterOptions,
  getProgressStatusFilterOptions,
} from '../utils/filter-options';

interface VehiculeCheckTableViewProps<
  TSchema extends FieldValues,
> extends TableViewProps<TSchema> {
  cardTitle?: React.ReactNode;
  receivedVehiculeId?: string;
  receivedActionUserId?: string;
}

export const VehiculeCheckTableView = <TSchema extends FieldValues>({
  t,
  useGetAllData,
  onGetTotalActive,
  tableHeaders: TableHeaders,
  tableData: TableData,
  pendingDelete,
  onDelete,
  canSelectItems = true,
  cardTitle,
  receivedVehiculeId,
  receivedActionUserId,
}: VehiculeCheckTableViewProps<TSchema>) => {
  const { pathname } = useLocation();
  let { vehiculeId } = useParams();

  const isRequestListPage = pathname.startsWith('/vehicule-check-requests');

  if (receivedVehiculeId) {
    vehiculeId = receivedVehiculeId;
  }

  const { pageSize: limit, setPageSize } = usePageSize();
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [totalPages, setTotalPages] = useState<number>();
  const { isAdmin, isGroupManager, isCompanyManager } = useUserRole();
  const [currentPage, setCurrentPage] = useState<number>(1);

  useEffect(() => {
    setCurrentPage(1);
  }, [isRequestListPage]);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  /**
   * === Filter Section ===
   */
  const {
    debouncedFilter,
    setDebouncedFilter,
    filtersEmpty,
    clearFilters,
    key,
    startDate,
    endDate,
    groupId,
    companyId,
    branchId,
    clientType,
    progressStatus,
    humanModerationStatus,
    reportValidated,
    damageStatus,
    searchByKey,
    searchStartDate,
    searchEndDate,
    searchByGroup,
    searchByCompany,
    searchByBranch,
    searchByClientType,
    searchByProgressStatus,
    searchByHumanModerationStatus,
    searchByReportValidated,
    searchByDamageStatus,
  } = useHistoryFilter({
    historyType: HistoryType.VEHICULE_CHECK,
    setCurrentPage,
  });

  useEffect(() => {
    setDebouncedFilter((prev) => ({
      ...prev,
      vehiculeId,
      actionUserId: receivedActionUserId,
      groupId,
      companyId,
      branchId,
      clientType: vehiculeId
        ? prev?.clientType
        : clientType
          ? clientType
          : isRequestListPage
            ? ActionUserType.INDIVIDUAL
            : undefined,
      isRequestList: isRequestListPage,
      progressStatus,
      humanModerationStatus,
      reportValidated,
      // Only on the inspection list (the filter is not displayed on the
      // request list, which shares the same stored filters)
      damageStatus: isRequestListPage ? undefined : damageStatus,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isRequestListPage,
    receivedVehiculeId,
    receivedActionUserId,
    groupId,
    companyId,
    branchId,
    clientType,
    progressStatus,
    humanModerationStatus,
    reportValidated,
    damageStatus,
  ]);

  const advancedFiltersCount = [
    startDate || endDate,
    !isRequestListPage && damageStatus,
    humanModerationStatus,
    reportValidated,
    groupId,
    companyId,
    branchId,
  ].filter(Boolean).length;
  /**
   * === END OF Filter Section ===
   */

  /**
   * === Data Fetching section ===
   */
  const {
    data: allData,
    isLoading: isLoadingAllData,
    isPending: isPendingAllData,
  } = useGetAllData(
    debouncedFilter,
    currentPage,
    limit,
    sortColumn,
    sortDirection,
  );

  const { data: allGroups, isLoading: isLoadingAllGroups } = useListAllGroups();
  const { data: allCompanies, isLoading: isLoadingAllCompanies } =
    useListAllCompanies({
      groupId: debouncedFilter?.groupId,
    });
  const { data: allBranches, isLoading: isLoadingAllBranches } =
    useListAllBranches({
      groupId: debouncedFilter?.groupId,
      companyId: debouncedFilter?.companyId,
    });
  /**
   * === END OF Data Fetching section ===
   */

  /**
   * === Pagination Section ===
   */
  useEffect(() => {
    setTotalPages(allData?.data?.totalPages);
    onGetTotalActive?.(allData?.data?.['total']);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allData]);
  /**
   * === END OF Pagination Section ===
   */

  /**
   * === Sorting Section ===
   */
  const handleSort = (column: string) => {
    setSortColumn(column);
    setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  };
  /**
   * === END OF Sorting Section ===
   */

  return (
    <>
      <Card bodyClassName="flex flex-col gap-5" title={cardTitle}>
        <div className="flex flex-wrap justify-between gap-3.5">
          <div className="flex gap-3.5 max-1.5xl:w-full max-1.5xl:justify-between">
            <TableTitle
              text={
                isRequestListPage
                  ? t('common:vehiculeCheckRequests')
                  : t('common:inspections')
              }
              infoTip={
                isRequestListPage
                  ? INSPECTION_TIPS.requestsTable
                  : INSPECTION_TIPS.inspectionsTable
              }
            />
            <div className="hidden max-md:block">
              <ClearButton disabled={filtersEmpty} onClick={clearFilters} />
            </div>
          </div>
          <div className="flex flex-wrap gap-3.5 ml-auto max-1.5xl:ml-0 max-1.5xl:w-full">
            <div className="flex items-center flex-1 gap-3.5 min-w-[300px] max-1.5xl:min-w-0 max-1.5xl:basis-full">
              <Input
                placeholder={t('common:searchLicensePlateOrVin')}
                type="text"
                name="search"
                autoComplete="off"
                leftIcon={
                  <i className="ki-outline ki-magnifier text-lg text-gray-8990A8" />
                }
                inputWrapperClassName="w-full"
                inputClassName="h-[36px] bg-gray-FCFCFC dark-mode:bg-coal-500"
                value={key}
                onChange={(e) => searchByKey(e.target.value)}
              />
              <div className="hidden max-1.5xl:block max-md:hidden">
                <ClearButton disabled={filtersEmpty} onClick={clearFilters} />
              </div>
            </div>
            {!isRequestListPage && (
              <RichSelect
                containerClassName="w-auto min-w-[190px] max-1.5xl:min-w-0 max-sm:basis-full"
                placeholder={t('common:proAndIndividual')}
                aria-label={t('common:type')}
                options={getClientTypeFilterOptions(t)}
                value={clientType}
                onChange={searchByClientType}
              />
            )}
            <RichSelect
              containerClassName="w-auto min-w-[230px] max-1.5xl:min-w-0 max-sm:basis-full"
              placeholder={t('inspection:allProgressStatuses')}
              aria-label={t('common:making')}
              menuWidth={320}
              options={getProgressStatusFilterOptions(t, isRequestListPage)}
              value={progressStatus}
              onChange={searchByProgressStatus}
            />
            <div className="flex justify-end max-sm:w-full">
              <AdvancedFilterToggle
                open={showAdvancedFilters}
                activeCount={advancedFiltersCount}
                onToggle={() => setShowAdvancedFilters((prev) => !prev)}
              />
            </div>
            <div className="max-1.5xl:hidden">
              <ClearButton disabled={filtersEmpty} onClick={clearFilters} />
            </div>
          </div>
        </div>
        <AdvancedFilterPanel open={showAdvancedFilters}>
          <VehicleCheckFilter
            t={t}
            isLoadingAllGroups={isLoadingAllGroups}
            isLoadingAllCompanies={isLoadingAllCompanies}
            isLoadingAllBranches={isLoadingAllBranches}
            humanModerationStatusOptions={getInspectionModerationStatus(t)}
            reportValidatedOptions={getInspectionValidationStatus(t)}
            groupOptions={mapFilterSelect(allGroups?.data)}
            companyOptions={mapFilterSelect(allCompanies?.data)}
            branchOptions={mapFilterSelect(allBranches?.data)}
            displayGroupFilter={isAdmin || isGroupManager}
            displayCompanyFilter={isAdmin || isGroupManager || isCompanyManager}
            displayBranchFilter
            displayDamageStatusFilter={!isRequestListPage}
            damageStatus={damageStatus}
            humanModerationStatus={humanModerationStatus}
            reportValidated={reportValidated}
            groupId={groupId}
            companyId={companyId}
            branchId={branchId}
            startDate={startDate}
            endDate={endDate}
            searchStartDate={searchStartDate}
            searchEndDate={searchEndDate}
            searchByDamageStatus={searchByDamageStatus}
            searchByHumanModerationStatus={searchByHumanModerationStatus}
            searchByReportValidated={searchByReportValidated}
            searchByGroup={searchByGroup}
            searchByCompany={searchByCompany}
            searchByBranch={searchByBranch}
          />
        </AdvancedFilterPanel>
        <div>
          <Datatable
            t={t}
            headers={
              <TableHeaders
                t={t}
                onSort={handleSort}
                sortDirection={sortDirection}
              />
            }
            data={
              isLoadingAllData ? (
                <></>
              ) : !allData?.data?.data?.length ? null : (
                <TableData
                  t={t}
                  data={allData?.data?.data}
                  canSelectItems={canSelectItems}
                  pendingDelete={pendingDelete}
                  onDelete={onDelete}
                />
              )
            }
            canSelectItems={canSelectItems}
            showPagination={allData?.data?.data?.length > 0 && totalPages > 1}
            totalPages={totalPages}
            currentPage={currentPage}
            onPageChange={(page) => setCurrentPage(page)}
            totalItems={isLoadingAllData ? undefined : allData?.data?.['total']}
            pageSize={limit}
            onPageSizeChange={setPageSize}
          />
        </div>
      </Card>
      {(isLoadingAllData || isPendingAllData || pendingDelete) && <Loader />}
    </>
  );
};
